import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Eyebrow } from "../components/Reveal";

const STEPS = [
    { n: "01", title: "DISCOVER", desc: "We learn your vision." },
    { n: "02", title: "DESIGN", desc: "We create the experience." },
    { n: "03", title: "DEVELOP", desc: "We bring everything to life." },
    { n: "04", title: "LAUNCH", desc: "Your vision goes online." },
    { n: "05", title: "GROW", desc: "We continue supporting your digital presence." },
];

const Step = ({ s, i, progress, reduce }) => {
    const t0 = 0.06 + i * 0.17;
    const t1 = t0 + 0.15;
    const opacity = useTransform(progress, [t0, t1], [0.16, 1]);
    const y = useTransform(progress, [t0, t1], [26, 0]);
    const dotScale = useTransform(progress, [t0, t1], [0.5, 1]);
    const glow = useTransform(progress, [t0, t1], [0, 1]);

    return (
        <motion.div
            data-testid={`process-step-${i}`}
            className="relative pl-12 lg:pl-0 lg:pt-14"
            style={reduce ? {} : { opacity, y }}
        >
            <motion.span
                style={reduce ? {} : { scale: dotScale, opacity: glow }}
                className="absolute left-0 top-1 flex h-7 w-7 items-center justify-center rounded-full border border-[#0066FF]/50 bg-white shadow-[0_0_18px_rgba(0,102,255,0.35)] lg:top-0"
            >
                <span className="h-2 w-2 rounded-full bg-[#0066FF]" />
            </motion.span>
            <span
                className="absolute left-0 top-1 hidden h-7 w-7 items-center justify-center rounded-full border border-[#0A0F1D]/15 lg:top-0 lg:flex"
                aria-hidden="true"
            />
            <p className="font-mono text-xs tracking-[0.35em] text-[#0066FF]">{s.n}</p>
            <h3 className="mt-3 font-display text-xl font-extrabold tracking-wide text-[#0A0F1D] sm:text-2xl">{s.title}</h3>
            <p className="mt-2 max-w-[240px] text-sm leading-relaxed text-slate-600">{s.desc}</p>
        </motion.div>
    );
};

export const Process = () => {
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
    const lineGrow = reduce ? 1 : scrollYProgress;

    return (
        <section id="process" ref={ref} className="relative lg:h-[240vh]" data-testid="process-section">
            <div className="relative overflow-hidden py-24 md:py-32 lg:sticky lg:top-0 lg:flex lg:h-screen lg:flex-col lg:justify-center lg:py-0">
                <div className="absolute right-0 top-24 h-72 w-72 rounded-full bg-[#0066FF]/10 blur-[110px]" aria-hidden="true" />
                <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Eyebrow>The Process</Eyebrow>
                    <h2 className="mt-6 font-display text-4xl font-extrabold tracking-tight text-[#0A0F1D] sm:text-5xl lg:text-6xl">
                        FROM IDEA<br />TO <span className="text-electric-grad">ONLINE.</span>
                    </h2>

                    <div className="relative mt-14 lg:mt-24">
                        <div
                            className="absolute left-[13px] top-2 bottom-2 w-px bg-[#0A0F1D]/10 lg:left-0 lg:right-0 lg:top-[13px] lg:bottom-auto lg:h-px lg:w-auto"
                            aria-hidden="true"
                        />
                        <motion.div
                            style={{ scaleY: lineGrow }}
                            className="absolute left-[13px] top-2 bottom-2 w-px origin-top bg-gradient-to-b from-sky-400 to-[#0066FF] lg:hidden"
                            aria-hidden="true"
                        />
                        <motion.div
                            style={{ scaleX: lineGrow }}
                            className="absolute left-0 right-0 top-[13px] hidden h-px origin-left bg-gradient-to-r from-sky-400 via-[#0066FF] to-sky-400 lg:block"
                            aria-hidden="true"
                        />
                        <div className="grid gap-12 lg:grid-cols-5 lg:gap-6">
                            {STEPS.map((s, i) => (
                                <Step key={s.n} s={s} i={i} progress={scrollYProgress} reduce={reduce} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
